import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createStructuredSelector } from "reselect";
import { getUserSelectedAccountCode } from "../../../../app/appSelectors.js";
import { getLiteTradeConfig, getLiteTradeSession } from "../../liteTradeSelectors.js";
import { connect } from "react-redux";
import useWebSocket from "react-use-websocket";
import moment from "moment/moment.js";
import { postLiteTradeRequest } from "../../liteTradeRequest.js";
import { Loader } from "../../../../components/Loader/Loader.jsx";
import { scaleLinear } from "d3-scale";
import LiteTradeChartTickerLine from "./LiteTradeChartTickerLine.jsx";

function LiteTradeChartTickerWrapper(props) {
    const {
        decimal,
        lite,
        symbol,
        onlyOneChartLine,

        liteTradeConfig,
        session,
        selectedAccountCode,
    } = props;

    const [countCandles] = useState(50);
    const {sendMessage, lastMessage} = useWebSocket(liteTradeConfig.wssMdUrl, {
        shouldReconnect: (e) => true,
    });
    const [chartLoader, setChartLoader] = useState(false);
    const lastSubscription = useRef();
    const [data, setData] = useState([]);

    const [xExtents, setXExtents] = useState([0, countCandles]);

    const xAccessor = useMemo(() => (d) => d?.pos, []);
    const xScale = useMemo(() => {
        return scaleLinear();
    }, []);

    const yAccessorAsk = useMemo(() => (d) => d?.ask, []);
    const yAccessorBid = useMemo(() => (d) => d?.bid, []);

    const yExtentsCalculator = useCallback(({plotData}) => {
        if (!plotData) {
            return [];
        }
        let min, max;
        for (const {ask, bid} of plotData) {
            if (min === undefined && !isNaN(bid)) min = bid;
            if (max === undefined && !isNaN(ask)) max = ask;
            if (bid !== undefined && min > bid) min = bid;
            if (ask !== undefined && max < ask) max = ask;
        }
        if (min === undefined) min = 0;
        if (max === undefined) max = 0;

        const padding = (max - min) * 0.1;
        return [min - padding, max + padding];
    }, []);

    const chartMargin = useMemo(() => {
        const price = data[data.length - 1]?.ask;
        return {
            top: 0,
            right: ((price?.toFixed?.(0).length || 0) + decimal + 1) * 8 + 4,
            bottom: lite ? 0 : 40,
            left: 0
        };
    }, [data, decimal, lite]);

    const setMarketDataEventsHandler = useCallback((data, historical = false) => {
        if (data?.[0]?.symbol !== symbol) {
            return;
        }
        let formatted = data
            .filter(item => item.ask || item.close)
            .map(item => ({
                timeStamp: (new Date(item.time)).getTime(),
                ...item,
            }));
        if (!formatted.length) return;
        if (formatted.length > 1) data.reverse();

        setData(prevState => {
            if (!prevState.length || (prevState[0]?.candleType !== formatted[0]?.candleType)) {
                return formatted.slice(-countCandles).map((item, pos) => ({
                    ...item,
                    pos,
                }));
            } else {
                const mergedData = [...prevState];
                formatted.forEach(item => {
                    historical ? mergedData.unshift(item) : mergedData.push(item);
                });
                return mergedData.slice(-countCandles).map((item, pos) => ({
                    ...item,
                    pos,
                }));
            }
        });
    }, [countCandles, symbol]);

    const getHistoricalData = useCallback((from, to = from) => {
        const params = {
            account: `default:${selectedAccountCode}`,
            symbols: [symbol],
            eventTypes: [
                {
                    type: "Quote",
                    format: "COMPACT",
                    fromTime: moment(from).add(-30, 'minutes').startOf('minutes').toISOString(),
                    toTime: moment(to).add(1, 'minutes').startOf('minutes').toISOString(),
                }
            ],
        };
        postLiteTradeRequest('/marketdata', params)
            .then(res => {
                if (res.events.length) {
                    setMarketDataEventsHandler(res.events, true);
                    setChartLoader(false);
                }
            })
            .catch(e => {
                console.error('cl e', e);
            });

    }, [selectedAccountCode, symbol, setMarketDataEventsHandler]);

    useEffect(() => {
        if (!selectedAccountCode) {
            return;
        }
        setChartLoader(true);
        if (lastSubscription.current) {
            sendMessage(JSON.stringify({
                type: "MarketDataCloseSubscriptionRequest",
                refRequestId: lastSubscription.current.requestId,
                session: session.sessionToken,
            }));
        }
        const requestId = `MarketDataTicker-${selectedAccountCode}`;
        const subscriptionParams = {
            type: "MarketDataSubscriptionRequest",
            requestId,
            payload: {
                account: `default:${selectedAccountCode}`,
                symbols: [symbol],
                eventTypes: [{
                    type: "Quote",
                    format: "COMPACT",
                }]
            },
            session: session.sessionToken,
        };
        sendMessage(JSON.stringify(subscriptionParams));
        lastSubscription.current = subscriptionParams;

        const to = moment().toISOString();
        getHistoricalData(to);
    }, [lite, symbol, sendMessage, selectedAccountCode, session.sessionToken, getHistoricalData]);

    // New messages handler
    useEffect(() => {
        if (lastMessage !== null) {
            // console.log('cl lastMessage');
            const lastMessageData = JSON.parse(lastMessage.data);
            switch (lastMessageData.type) {
                case 'MarketData':
                    setMarketDataEventsHandler(lastMessageData.payload.events);
                    break;
                default:
                    console.log('cl [SOCKET] Unknown message', lastMessageData);
            }
        }
    }, [lastMessage, sendMessage, session.sessionToken, setMarketDataEventsHandler]);

    return <div>
        {
            !data.length && <Loader show={true}/>
        }

        <LiteTradeChartTickerLine data={data}
                                  xAccessor={xAccessor}
                                  xExtents={xExtents}
                                  xScale={xScale}
                                  yAccessorAsk={yAccessorAsk}
                                  yAccessorBid={yAccessorBid}
                                  yExtentsCalculator={yExtentsCalculator}
                                  decimal={decimal}
                                  margin={chartMargin}
                                  onlyOneChartLine={onlyOneChartLine}
        />

    </div>
}

const mapStateToProps = createStructuredSelector({
    selectedAccountCode: getUserSelectedAccountCode,
    session: getLiteTradeSession,
    liteTradeConfig: getLiteTradeConfig,
});

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(memo(LiteTradeChartTickerWrapper));
